import { writeFile, readFile } from "fs";
import { StrapiApi } from "./strapiApi";
import { CityType } from "../types/strapi/CityType";
import { isThisQuarter } from "date-fns";
import { PageBaseType, PlaceType } from "../types";

const MAIN_STORE_PATH = `./store`;

type StoreName =
  | "cities"
  | "vtc"
  | "taxi"
  | "taxi-moto"
  | "places"
  | "place-pages";

type SyncState = {
  lastSync: number;
  firstSync: number;
  nb: number;
  ids: string[];
};

function storeFile(filePath: string, data: any): Promise<void> {
  return new Promise((res, rej) => {
    writeFile(filePath, JSON.stringify(data), (err) => {
      if (err) {
        rej(err);
      } else {
        res();
      }
    });
  });
}

function getFile<T>(filePath: string): Promise<T> {
  //console.log(filePath);
  return new Promise((res, rej) => {
    readFile(filePath, { encoding: "utf-8" }, (err, data) => {
      if (err) {
        console.error(filePath, err);
        rej(err);
      } else {
        res(JSON.parse(data));
      }
    });
  });
}

class Store<T> {
  STORE_PATH: string;
  SYNC_DATE_PATH: string;
  STORE_NAME: StoreName;
  constructor(storeName: StoreName) {
    this.STORE_NAME = storeName;
    this.STORE_PATH = `${MAIN_STORE_PATH}/${storeName}`;
    this.SYNC_DATE_PATH = `${MAIN_STORE_PATH}/${storeName}-last-sync.json`;
  }

  _listFromStrapi(updatedAt: Date): Promise<T[]> {
    if (this.STORE_NAME === "cities") {
      return StrapiApi.collection_types
        .allCities([], "fr", updatedAt)
        .then((cities) => {
          return cities as unknown[] as T[];
        });
    }
    if (this.STORE_NAME === "taxi-moto") {
      return StrapiApi.collection_types
        .allMotoTaxiPages([], "fr", updatedAt)
        .then((pages) => {
          return pages as unknown[] as T[];
        });
    }
    if (this.STORE_NAME === "vtc") {
      return StrapiApi.collection_types
        .allVTCPages([], "fr", updatedAt)
        .then((pages) => {
          return pages as unknown[] as T[];
        });
    }
    if (this.STORE_NAME === "taxi") {
      return StrapiApi.collection_types
        .allTaxiPages([], "fr", updatedAt)
        .then((pages) => {
          return pages as unknown[] as T[];
        });
    }
    if (this.STORE_NAME === "places") {
      return StrapiApi.collection_types
        .allPlaces([], "fr", updatedAt)
        .then((places) => {
          return places as unknown[] as T[];
        });
    }
    if (this.STORE_NAME === "place-pages") {
      return StrapiApi.collection_types
        .allPlacePages([], "fr", updatedAt)
        .then((page) => {
          return page as unknown[] as T[];
        });
    }
    throw new Error(
      `listEndpointForStore : ${this.STORE_NAME} - NOT IMPLEMENTED BUT CALLED`
    );
  }

  _getFromStrapi(key: string): Promise<T> {
    if (this.STORE_NAME === "cities") {
      return StrapiApi.collection_types.city("fr", key).then((city) => {
        return city as unknown as T;
      });
    }
    if (this.STORE_NAME === "taxi-moto") {
      return StrapiApi.collection_types.motoTaxiPage("fr", key).then((page) => {
        return page as unknown as T;
      });
    }
    if (this.STORE_NAME === "vtc") {
      return StrapiApi.collection_types.VTCPage("fr", key).then((page) => {
        return page as unknown as T;
      });
    }
    if (this.STORE_NAME === "taxi") {
      return StrapiApi.collection_types.taxiPage("fr", key).then((page) => {
        return page as unknown as T;
      });
    }
    if (this.STORE_NAME === "places") {
      return StrapiApi.collection_types.place("fr", key).then((place) => {
        return place as unknown as T;
      });
    }
    if (this.STORE_NAME === "place-pages") {
      return StrapiApi.collection_types.placePage("fr", key).then((place) => {
        return place as unknown as T;
      });
    }
    throw new Error(
      `getEndpointForStore : ${this.STORE_NAME} - NOT IMPLEMENTED BUT CALLED`
    );
  }

  getSyncState(): Promise<SyncState> {
    return getFile<SyncState>(this.SYNC_DATE_PATH).catch(() => {
      return {
        lastSync: new Date(0).getTime(),
        firstSync: new Date().getTime(),
        nb: 0,
        ids: [],
      };
    });
  }

  getEntryFromStore(slug: string) {
    return getFile<T>(`${this.STORE_PATH}/${slug}.json`);
  }

  async storeEntry(slug: string, data: T, updateSyncState: boolean) {
    if (updateSyncState) {
      const syncState = await this.getSyncState();
      if (syncState.ids.indexOf(slug) === -1) {
        syncState.ids.push(slug);
        syncState.nb = syncState.ids.length;
        syncState.lastSync = Date.now();
        storeFile(this.SYNC_DATE_PATH, syncState);
      }
    }

    return storeFile(`${this.STORE_PATH}/${slug}.json`, data);
  }

  async getEntry(slug: string): Promise<T | null> {
    const city = await this.getEntryFromStore(slug);
    if (city) {
      return city;
    }
    console.log(this.STORE_NAME, slug, "not found, fetching STRAPI");
    const updated = await this._getFromStrapi(slug);
    if (!updated) {
      return null;
    }
    this.storeEntry(slug, updated, true);
    return updated;
  }

  async listEntriesFromStore(): Promise<T[]> {
    const syncState = await this.getSyncState();
    const updatedEntries = await this._listFromStrapi(
      new Date(syncState.lastSync)
    );

    for (const entry of updatedEntries) {
      const key = (entry as any)["slug"];
      this.storeEntry(key, entry, false);
      if (syncState.ids.indexOf(key) === -1) {
        syncState.ids.push(key);
      }
    }
    syncState.nb = syncState.ids.length;
    syncState.lastSync = Date.now();
    storeFile(this.SYNC_DATE_PATH, syncState);

    if (updatedEntries.length === syncState.nb) {
      return updatedEntries;
    }

    const entries: T[] = [];

    for (const slug of syncState.ids) {
      const city = await this.getEntry(slug);
      if (city) {
        entries.push(city);
      }
    }

    return entries;
  }
}

export const stores = {
  cities: new Store<CityType>("cities"),
  taximoto: new Store<PageBaseType>("taxi-moto"),
  vtc: new Store<PageBaseType>("vtc"),
  taxi: new Store<PageBaseType>("taxi"),
  places: new Store<PlaceType>("places"),
  placePages: new Store<PageBaseType>("place-pages"),
};

export async function sync() {
  console.log("Start syncing all collections types to store as JSON");
  await stores.cities.listEntriesFromStore();
  console.log("cities ok");
  await stores.taximoto.listEntriesFromStore();
  console.log("taximoto ok");
  await stores.vtc.listEntriesFromStore();
  console.log("vtc ok");
  await stores.taxi.listEntriesFromStore();
  console.log("taxi ok");
  await stores.places.listEntriesFromStore();
  console.log("places ok");
  await stores.placePages.listEntriesFromStore();
  console.log("placePages ok");
}
